import React from 'react';

interface ButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  icon?: string;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

export default function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  type = 'button',
  className = '',
}: ButtonProps) {
  const baseStyles = 'font-semibold rounded-md transition-all duration-150 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-[0.98] lg:active:scale-100';
  
  const variantStyles = {
    primary: 'bg-brand-600 hover:bg-brand-700 text-white focus:ring-accent-500 border border-transparent',
    secondary: 'bg-ink-100 hover:bg-ink-200 text-ink-800 focus:ring-ink-400 border border-transparent',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500 border border-transparent',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border border-transparent',
    warning: 'bg-accent-500 hover:bg-accent-600 text-white focus:ring-accent-500 border border-transparent',
    info: 'bg-sky-600 hover:bg-sky-700 text-white focus:ring-sky-500 border border-transparent',
    outline: 'bg-white hover:bg-ink-50 text-ink-700 focus:ring-ink-400 border border-ink-300',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm min-h-[40px] lg:min-h-0',
    md: 'px-4 py-2 text-sm min-h-[44px] lg:min-h-0',
    lg: 'px-5 py-2.5 text-base min-h-[48px] lg:min-h-0',
  };

  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${disabledStyles} ${widthStyles} ${className}`}
    >
      {icon && iconPosition === 'left' && (
        <i className={`fas ${icon} ${children ? 'mr-2' : ''}`}></i>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <i className={`fas ${icon} ${children ? 'ml-2' : ''}`}></i>
      )}
    </button>
  );
}
